import React, { useState, useRef } from "react";
import Logo from "./Logo";
import DesktopNavigation from "./DesktopNavigation";
import MobileMenuButton from "./MobileMenuButton";
import LoginButton from "./LoginButton";
import NavbarStyles from "./NavbarStyles";
import { NavItem } from "./NavbarData";
import NavbarHover from "./NavbarHover";
import MobileNavigation from "./MobileNavigation/MobileNavigation";
import { useAccountStore } from "../../store/useAccountStore";
import { Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, LogOut } from "lucide-react";

interface NavbarProps {
  isVisible?: boolean; // Optional prop for scroll visibility
}

const Navbar: React.FC<NavbarProps> = ({ isVisible = true }) => {
  const [activeItem, setActiveItem] = useState<string>("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { user, clearUser } = useAccountStore();
  const navigate = useNavigate(); // for redirect after logout

  const navItems: NavItem[] = user ? [
    { id: "about", label: "About Us", to: "/" },
    { id: "events", label: "Events", to: "/news-events" },
    { id: "ethics-compliance", label: "Ethics & Compliance", to: "/" },
    { id: "contact", label: "Contact Us", to: "/" },
    { id: 'training', label: 'Learning Centre', to: "/" },
  { id: 'downloads', label: 'Downloads', to: '/downloads' },
      { id: "blogs", label: "Blogs", to: "/blogs" }
  ]:([
    { id: "about", label: "About Us", to: "/" },
    { id: "ethics-compliance", label: "Ethics & Compliance", to: "/" },
    { id: "contact", label: "Contact Us", to: "/" }
  
  ]);

  const handleNavClick = (itemId: string) => {
    setActiveItem(itemId);
    setIsMobileMenuOpen(false);
    setIsDropdownVisible(false);
    setHoveredItem(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMouseEnter = (itemId: string) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredItem(itemId);
    setIsDropdownVisible(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
      setIsDropdownVisible(false);
    }, 200);
  };

  const handleDropdownMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsDropdownVisible(true);
  };

  const handleDropdownMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsDropdownVisible(false);
      setHoveredItem(null);
    }, 100);
  };

  // 🔒 Logout function
  const handleLogout = () => {
    clearUser();          // clears user data
    navigate("/");   // redirect to login page
  };

  return (
    <div className="relative">
      <nav
        className={`bg-white fixed left-0 right-0 top-0 z-50 font-poppins h-16 lg:h-[80px] xl:h-[80px] w-full transform transition-transform duration-300 ease-in-out ${
          isVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="max-w-full mx-4 md:mx-5 lg:mx-9 xl:mx-10 h-full">
          <div className="flex justify-between items-center h-full">
            {/* Logo */}
            <button  className="flex-shrink-0">
              <Logo />
            </button>


            <div className="hidden lg:flex flex-shrink-0 gap-4 items-center">
              {user?.name && (
                <Link
                  to="/select-dashboard"
                  className="flex gap-3 lg:text-sm xl:text-base lg:py-2 xl:py-2 px-8 lg:px-12 xl:px-4 bg-navgreen text-white font-medium rounded-md transition-all duration-300 hover:shadow-lg transform hover:scale-105"
                >
                  <LayoutDashboard /> Portal Hub
                </Link>
              )}

              {user?.name ? (
                <div className="flex items-center gap-2">
                  <img src="assets/user-4.svg" className="w-8 h-8" />
                  <p>{user.name}</p>

                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-1 text-sm text-red-600 font-medium hover:text-red-800"
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              ) : (
                <LoginButton />
              )}
            </div>

            {/* Mobile Menu Button */}
            <MobileMenuButton isOpen={isMobileMenuOpen} onToggle={toggleMobileMenu} />
          </div>
        </div>

        <div className="max-w-full h-1/2 bg-primaryGreen w-screen flex items-center">
          <DesktopNavigation
            navItems={navItems}
            activeItem={activeItem}
            hoveredItem={hoveredItem}
            isDropdownVisible={isDropdownVisible}
            onNavClick={handleNavClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
          {hoveredItem && isDropdownVisible && isVisible && (
            <NavbarHover
              navId={hoveredItem}
              isVisible={isDropdownVisible}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            />
          )}
        </div>

        <MobileNavigation
          navItems={navItems}
          activeItem={activeItem}
          isMobileMenuOpen={isMobileMenuOpen}
          onNavClick={handleNavClick}
        />
      </nav>

      <NavbarStyles />
    </div>
  );
};

export default Navbar;
